import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { useSubscription } from '@meraki-internal/state';
import { CaremapInsightsModelFactory } from './CaremapInsightsModelFactory';
import { IInsight } from './IInsight';
import { insightTypeMeta } from './insightTypeMeta';
import { ChevronRightIcon } from '../theme/Icons';
import { QueryParamDrivenDrawerHelper } from '../components/query-param-driver-drawer/QueryParamDrivenDrawerHelper';

const useStyles = createUseStyles({
    section: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 10,
    },
    card: {
        background: 'rgba(var(--ion-color-primary-rgb), 0.08)',
        borderRadius: 14,
        padding: '12px 14px',
        cursor: 'pointer',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 12.5,
        fontWeight: 600,
        color: 'var(--ion-color-primary)',
        marginBottom: 8,
    },
    avatar: {
        width: 22,
        height: 22,
        borderRadius: '50%',
        objectFit: 'cover' as const,
        flexShrink: 0,
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 9,
    },
    typeIcon: {
        flexShrink: 0,
        display: 'inline-flex',
    },
    title: {
        flex: 1,
        minWidth: 0,
        fontSize: 14.5,
        fontWeight: 600,
        lineHeight: 1.32,
        color: 'var(--ion-text-color)',
    },
    lead: {
        fontSize: 12.5,
        color: 'var(--ion-color-step-600)',
        marginTop: 6,
        lineHeight: 1.45,
    },
});

interface INoteInsightCardsProps {
    noteId: string;
}

// When a note is one of the items behind an insight, show a pointer into that insight -- not a triage
// card. This is a "look what Bea connected" affordance; the reading/feedback happens in the drawer.
export const NoteInsightCards: FC<INoteInsightCardsProps> = ({ noteId }) => {
    const classes = useStyles();

    const insightsFactory = useInstance(CaremapInsightsModelFactory);
    useSubscription(() => insightsFactory);

    const drawerHelper = useInstance(QueryParamDrivenDrawerHelper);

    // forActiveCaremap throws rather than returning falsy when there's no active caremap,
    // so callers rendered outside an active caremap will error rather than silently no-op.
    const insightModel = insightsFactory.forActiveCaremap();
    const insights = insightModel.getForNote(noteId);

    if (insights.length === 0) {
        return null;
    }

    const onOpen = (insight: IInsight) => {
        drawerHelper.open({ mode: 'view-insight', insightId: insight.insightId, surface: 'note' });
    };

    return (
        <div className={classes.section}>
            {insights.map(insight => (
                <NoteInsightPointer key={insight.insightId} insight={insight} onOpen={() => onOpen(insight)} />
            ))}
        </div>
    );
};

const NoteInsightPointer: FC<{ insight: IInsight; onOpen: () => void }> = ({ insight, onOpen }) => {
    const classes = useStyles();
    const { Icon, noteIntro } = insightTypeMeta(insight.type);

    return (
        <div
            className={classes.card}
            data-type="note-insight-pointer"
            data-insight-id={insight.insightId}
            onClick={onOpen}
        >
            <div className={classes.header}>
                <img src="/assets/images/help/helper-bea.png" alt="Bea" className={classes.avatar} />
                {noteIntro}
            </div>
            <div className={classes.titleRow}>
                <span className={classes.typeIcon}><Icon color="medium" size="small" /></span>
                <span className={`${classes.title} mp-mask`}>{insight.title}</span>
                <ChevronRightIcon color="primary" size="small" />
            </div>
            <div className={classes.lead}>This note is one of the items it’s based on. Tap to see the whole pattern.</div>
        </div>
    );
};
